import { useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import Modal from '@/components/ui/modal'
import { Input } from '@/components/ui/input'
import { extractApiErrorMessage } from '@/utils/apiError'
import {
  DEFAULT_APPOINTMENT_WEBHOOK_EVENTS,
  getAppointmentWebhookEventOptions,
  getVerticalAppointmentWebhookCopy,
  ORDER_WEBHOOK_EVENT_OPTIONS,
  type SubscribableWebhookEventName,
  type WebhookEndpoint,
} from '@/services/orderWebhooksApi'
import {
  useCreateWebhookEndpoint,
  useUpdateWebhookEndpoint,
} from '@/hooks/useOrderWebhooks'
import { useVerticalUI } from '@/lib/verticalUI'

interface WebhookEndpointFormModalProps {
  endpoint?: WebhookEndpoint
  onClose: () => void
  onSaved: (result: { endpoint: WebhookEndpoint; secret?: string }) => void
}

function validateUrl(value: string): string | null {
  const trimmed = value.trim()
  if (!trimmed) return 'La dirección es obligatoria.'
  let parsed: URL
  try {
    parsed = new URL(trimmed)
  } catch {
    return 'Escribe una dirección completa, por ejemplo https://mi-sistema.com/avisos.'
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    return 'La dirección debe empezar por https://.'
  }
  return null
}

export function WebhookEndpointFormModal({
  endpoint,
  onClose,
  onSaved,
}: WebhookEndpointFormModalProps) {
  const isEdit = Boolean(endpoint)
  const { slug } = useVerticalUI()
  const { isAppointmentVertical, singularLabel, pluralLabel } =
    getVerticalAppointmentWebhookCopy(slug)

  const appointmentEventOptions = getAppointmentWebhookEventOptions(singularLabel)
  const defaultEvents: SubscribableWebhookEventName[] = isAppointmentVertical
    ? [...DEFAULT_APPOINTMENT_WEBHOOK_EVENTS]
    : ['order.created']

  const createMutation = useCreateWebhookEndpoint()
  const updateMutation = useUpdateWebhookEndpoint()

  const [url, setUrl] = useState(endpoint?.url ?? '')
  const [description, setDescription] = useState(endpoint?.description ?? '')
  const [events, setEvents] = useState<SubscribableWebhookEventName[]>(
    (endpoint?.events as SubscribableWebhookEventName[] | undefined) ?? defaultEvents
  )
  const [includeAppointmentNotes, setIncludeAppointmentNotes] = useState<boolean>(
    Boolean(endpoint?.includeAppointmentNotes)
  )
  const [formError, setFormError] = useState<string | null>(null)

  const isPending = createMutation.isPending || updateMutation.isPending
  const hasSelectedAppointmentEvent = events.some((event) => event.startsWith('appointment.'))

  function toggleEvent(value: SubscribableWebhookEventName, checked: boolean) {
    setEvents((current) =>
      checked ? Array.from(new Set([...current, value])) : current.filter((item) => item !== value)
    )
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setFormError(null)

    const urlError = validateUrl(url)
    if (urlError) {
      setFormError(urlError)
      return
    }
    if (events.length === 0) {
      setFormError('Elige al menos un aviso para enviar.')
      return
    }

    const notesOptIn = hasSelectedAppointmentEvent ? includeAppointmentNotes : false

    try {
      if (endpoint) {
        const updated = await updateMutation.mutateAsync({
          id: endpoint.id,
          input: {
            url: url.trim(),
            description: description.trim() || null,
            events,
            includeAppointmentNotes: notesOptIn,
          },
        })
        onSaved({ endpoint: updated })
      } else {
        const created = await createMutation.mutateAsync({
          url: url.trim(),
          description: description.trim() || null,
          events,
          includeAppointmentNotes: notesOptIn,
        })
        onSaved(created)
      }
    } catch (error) {
      setFormError(
        extractApiErrorMessage(error, 'No se pudo guardar el destino. Revisa la dirección.').message
      )
    }
  }

  const appointmentSection = (
    <div key="appointments-section" className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        Avisos de {pluralLabel}
      </p>
      {appointmentEventOptions.map((option) => {
        const checked = events.includes(option.value)
        return (
          <label
            key={option.value}
            className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50"
          >
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-gray-300 text-[#075E54] focus:ring-[#075E54]"
              checked={checked}
              disabled={isPending}
              onChange={(event) => toggleEvent(option.value, event.target.checked)}
            />
            <span>
              <span className="block text-sm font-medium text-gray-900">{option.label}</span>
              <span className="block text-xs text-gray-500">{option.description}</span>
            </span>
          </label>
        )
      })}
    </div>
  )

  const orderSection = (
    <div key="orders-section" className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        Avisos de pedidos
      </p>
      {ORDER_WEBHOOK_EVENT_OPTIONS.map((option) => {
        const checked = events.includes(option.value)
        return (
          <label
            key={option.value}
            className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50"
          >
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-gray-300 text-[#075E54] focus:ring-[#075E54]"
              checked={checked}
              disabled={isPending}
              onChange={(event) => toggleEvent(option.value, event.target.checked)}
            />
            <span>
              <span className="block text-sm font-medium text-gray-900">{option.label}</span>
              <span className="block text-xs text-gray-500">{option.description}</span>
            </span>
          </label>
        )
      })}
    </div>
  )

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!isPending) onClose()
      }}
      title={isEdit ? 'Editar destino' : 'Agregar destino'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="webhook-url" className="mb-1 block text-sm font-medium text-gray-900">
            Dirección que recibirá el aviso
          </label>
          <Input
            id="webhook-url"
            type="url"
            inputMode="url"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder={
              isAppointmentVertical
                ? 'https://mi-sistema.com/avisos/citas'
                : 'https://mi-sistema.com/avisos/pedidos'
            }
            autoFocus
            disabled={isPending}
            required
          />
          <p className="mt-1 text-xs text-gray-500">
            Debe ser una dirección pública con https. No se permiten direcciones internas ni de la red local.
          </p>
        </div>

        <div>
          <label
            htmlFor="webhook-description"
            className="mb-1 block text-sm font-medium text-gray-900"
          >
            Nombre para reconocerlo <span className="font-normal text-gray-500">(opcional)</span>
          </label>
          <Input
            id="webhook-description"
            value={description}
            maxLength={200}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={
              isAppointmentVertical
                ? 'Agenda, Google Calendar, Google Sheets…'
                : 'ERP, Google Sheets, Zapier…'
            }
            disabled={isPending}
          />
        </div>

        <fieldset className="space-y-4">
          <legend className="mb-2 text-sm font-medium text-gray-900">¿Qué avisos quieres recibir?</legend>
          {isAppointmentVertical
            ? [appointmentSection, orderSection]
            : [orderSection, appointmentSection]}
        </fieldset>

        {hasSelectedAppointmentEvent && (
          <div className="rounded-lg border border-amber-200 bg-amber-50/70 p-3">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-gray-300 text-[#075E54] focus:ring-[#075E54]"
                checked={includeAppointmentNotes}
                disabled={isPending}
                onChange={(event) => setIncludeAppointmentNotes(event.target.checked)}
              />
              <span>
                <span className="block text-sm font-medium text-gray-900">
                  Incluir el motivo de la {singularLabel.toLowerCase()} (notes)
                </span>
                <span className="block text-xs text-gray-600">
                  Desactivado por defecto para proteger datos sensibles en hojas compartidas o automatizaciones externas.
                </span>
              </span>
            </label>

            {slug === 'salud' && (
              <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-100/80 p-2 text-xs text-amber-950">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" aria-hidden="true" />
                <span>
                  <strong>Aviso de confidencialidad médica:</strong> el motivo puede incluir síntomas declarados por el paciente. Actívalo solo si el sistema receptor cumple con tus políticas de resguardo clínico.
                </span>
              </div>
            )}
          </div>
        )}

        {formError && (
          <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
            {formError}
          </p>
        )}

        <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
            Cancelar
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Agregar destino'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
